import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag, faHandHoldingDollar } from "@fortawesome/free-solid-svg-icons";
import { faSquare, faSquareCheck } from "@fortawesome/free-regular-svg-icons";

import CollapsingPills from "$components/table/cells/collapsing-pills";
import LargeNumberRange from "$components/table/cells/large-number-range";
import LogoCell from "$components/table/cells/logo";
import PredefinedNumberRange from "$components/table/cells/predefined-number-range";
import ProbabilityBubble from "$components/probability";

import { MultiRangeFilter, ProbabilityRange, SelectColumnFilter } from "$components/table/components/filters/filter";
import { getTerm } from "$stores/dictionary";
import { getExpertInIcon, getExpertDimensions } from "$components/profile-icon/lib/constants";
import { Checkbox } from "$components/form-inputs/plain-inputs";

export const buildDefaultColumns = ({ settings = {}, updateSelectedList, filterOptions, globalFilterRanges }) => {
    var columns = [
        {
            Header: (props) => {
                if (!props) {
                    return "";
                }
                const { data, selectedData } = props;
                const allDataSelected = data.every((d) => selectedData.some((p) => p.key === d.key));
                return (
                    <div className="bulk-select-header" onClick={() => updateSelectedList("bulk", data)}>
                        <FontAwesomeIcon icon={allDataSelected ? faSquareCheck : faSquare} />
                    </div>
                );
            },
            accessorKey: "key",
            id: "key",
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ({ data, value, rowIndex, isSelected }) => {
                if (data.userFlagged) {
                    return <FontAwesomeIcon icon={faFlag} />;
                }
                return (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <Checkbox
                            name={`select_${data.key}_${rowIndex}`}
                            value={isSelected}
                            onChange={(e) => {
                                updateSelectedList(isSelected ? "remove" : "add", [data]);
                            }}
                        />
                    </div>
                );
            },
            size: 50,
            fixedWidth: true,
            className: "-center",
        },
        {
            Header: () => getTerm("projectType"),
            accessorKey: "name", // accessor is the "key" in the data
            id: "name", // id is used as a sort key (i.e., actual api name)
            size: 120,
            Cell: ({ data, value }) => {
                return <div>{value}</div>;
            },
        },
        {
            Header: () => getTerm("projectTypeGroup"),
            accessorKey: "projectTypeGroup", // accessor is the "key" in the data
            id: "projectTypeGroup", // id is used as a sort key (i.e., actual api name)
            size: 120,
        },
        {
            Header: () => getTerm("parentClient"),
            accessorKey: "clientGroupName",
            id: "clientGroupName",
            Cell: ({ data, value }) => <LogoCell logoUrl={data?.clientLogoUrl} value={value} />,
        },
        {
            Header: () => getTerm("subClient"),
            accessorKey: "clientName",
            id: "clientName",
            className: "",
        },
        {
            Header: () => getTerm("serviceLine"),
            accessorKey: "serviceLine",
            size: 120,
            Filter: SelectColumnFilter,
            filterOptions: { options: filterOptions?.practice?.values || [] },
            enableSorting: false,
            Cell: ({ data, value }) => {
                const newData = value ? [{ id: value, text: value }] : [];
                return <CollapsingPills data={newData} maxVisible={2} keyPrefix="serviceLine" analyticsLabel={getTerm("serviceLines")} />;
            },
        },
        {
            Header: () => getTerm("industry"),
            accessorKey: "industries",
            id: "industries",
            size: 150,
            enableSorting: false,
            Cell: ({ data, value }) => {
                const pills = (value || []).map((i) => ({ id: i.id, text: i.text }));
                return <CollapsingPills data={pills} maxVisible={2} keyPrefix="industry" analyticsLabel={getTerm("industries")} />;
            },
        },
        {
            Header: () => getTerm("projectTypeGroup"),
            accessorKey: "practiceGroups",
            id: "practiceGroups",
            size: 150,
            enableSorting: false,
            Cell: ({ data, value }) => {
                const pills = (value || []).map((i) => ({ id: i.id, text: i.text }));
                return <CollapsingPills data={pills} maxVisible={2} keyPrefix="practiceGroup" analyticsLabel={getTerm("projectTypeGroups")} />;
            },
        },
        {
            Header: () => "Tags",
            accessorKey: "tags",
            size: 120,
            Filter: SelectColumnFilter,
            filterOptions: {
                options: [
                    { value: "requiresIndependence", text: "Check Conflicts" },
                    { value: "isCrossSell", text: "Cross-Sell" },
                ],
            },
            enableSorting: false,
            Cell: ({ data, value }) => {
                const tags = [];
                if (data.requiresIndependence) {
                    tags.push({ id: "independence", text: "Check Conflicts", modifierClassNames: ["-independence"] });
                }
                if (data.isCrossSell) {
                    tags.push({ id: "cross-sell", text: "Cross-Sell", icon: faHandHoldingDollar, modifierClassNames: ["-importance"] });
                }

                return <CollapsingPills data={tags} maxVisible={2} keyPrefix="tags" analyticsLabel="Tags" />;
            },
        },
        {
            Header: () => `${getTerm("subClient")} Size`,
            accessorKey: "clientSize",
            id: "clientSizeSortOrder",
            sortDescFirst: true,
            Filter: SelectColumnFilter,
            filterOptions: { options: filterOptions?.clientSize?.values },
            Cell: ({ data, value }) => {
                return <PredefinedNumberRange value={value} isHealthy={true} />;
            },
        },
        {
            Header: () => getTerm("averageRevenue"),
            accessorKey: "averageRevenue",
            id: "averageRevenue",
            sortDescFirst: true,
            Filter: MultiRangeFilter,
            filters: {
                min: globalFilterRanges?.averageRevenueMin ? Math.floor(globalFilterRanges?.averageRevenueMin) : "",
                max: globalFilterRanges?.averageRevenueMax ? Math.ceil(globalFilterRanges?.averageRevenueMax) : "",
                format: { isCurrency: true },
            },
            Cell: ({ data, value }) => {
                if (data.source === "Foley") return <span>--</span>;
                return <LargeNumberRange value={value} decimalPrecision={0} />;
            },
        },
        {
            Header: () => "Probability",
            accessorKey: "probability",
            id: "probability",
            sortDescFirst: true,
            size: 100,
            Filter: ProbabilityRange,
            filters: {
                min: 1,
                max: 3,
                format: { isCurrency: false },
            },
            Cell: ({ data, value }) => <ProbabilityBubble value={value} />,
        },
        {
            Header: () => "Source",
            accessorKey: "source",
            id: "source",
            size: 100,
            enableSorting: false,
            Filter: SelectColumnFilter,
            filterOptions: {
                options: [
                    { value: "Orgaimi", text: "Orgaimi" },
                    { value: "Foley", text: "Foley" },
                ],
            },
            Cell: ({ value }) => {
                if (!value) return null;
                const isOrgaimi = value === "Orgaimi";
                return (
                    <span
                        className={`source-badge ${isOrgaimi ? "-orgaimi" : "-foley"}`}
                        data-tooltip={`Recommended by ${value}`}
                    >
                        {value}
                    </span>
                );
            },
        },
    ];

    if (settings.visibleColumns && settings.visibleColumns.length > 0) {
        columns = columns.filter((c) => {
            return settings.visibleColumns.includes(c.accessorKey);
        });
    }

    if (Array.isArray(settings?.additionalColumns)) {
        settings.additionalColumns.forEach((c) => {
            const pos = c.position || columns.length;
            columns.splice(pos, 0, c);
        });
    }

    return columns;
};

export const experienceColumn = {
    Header: () => "Experience",
    accessorKey: "team",
    enableColumnFilter: false,
    enableSorting: false,
    id: "team",
    sortDescFirst: true,
    size: 100,
    position: 4,
    Cell: (data) => {
        const team = data.value || [];
        const value = team.reduce((exp, t) => {
            exp.push(...t.expertType);
            return exp;
        }, []);
        return (
            <div className="expert-in">
                {expertTypes.map((t) => {
                    const isExpert = value.some((v) => getExpertDimensions(v.id).includes(t.id));
                    return (
                        <div key={`expertIn${t.id}`} className={classNames({ "-active": isExpert })} data-tooltip={t.text}>
                            <span>
                                <FontAwesomeIcon icon={getExpertInIcon(t.id).icon} />
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    },
};
